import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Container, TextField, Typography, Alert } from '@mui/material';
import TempRegistrationService from '../services/tempRegistrationService';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const TempRegistration = () => {
  const query = useQuery();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState('verify'); // 'verify' | 'password' | 'done'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const token = query.get('token') || query.get('Token') || query.get('code') || '';

  const extractErrorMessage = (err, fallback = 'Es ist ein Fehler aufgetreten.') => {
    try {
      const detail = err?.response?.data?.detail;
      if (!detail) return err?.message || fallback;
      if (typeof detail === 'string') return detail;
      if (Array.isArray(detail)) {
        const msgs = detail.map((d) => {
          if (typeof d === 'string') return d;
          if (d && typeof d === 'object') return d.msg || d.message || '';
          return '';
        }).filter(Boolean);
        return msgs.join('; ') || fallback;
      }
      if (typeof detail === 'object') {
        if (Array.isArray(detail.errors) && detail.errors.length) {
          return `${detail.message || fallback}: ${detail.errors.join(', ')}`;
        }
        if (detail.message) return detail.message;
        // Letzter Ausweg: kompakte JSON-Darstellung
        return JSON.stringify(detail);
      }
      return String(detail);
    } catch (_) {
      return fallback;
    }
  };

  useEffect(() => {
    setError('');
    setInfo('');
  }, [step]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInfo('');
    try {
      const res = await TempRegistrationService.verify({ email, token });
      if (res.can_register) {
        setStep('password');
        setInfo('Verifizierung erfolgreich. Bitte legen Sie nun Ihr Passwort fest.');
      } else {
        setError(res.message || 'Registrierung ist nicht möglich.');
      }
    } catch (err) {
      setError(extractErrorMessage(err, 'Verifizierung fehlgeschlagen.'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInfo('');

    if (!password || password !== confirmPassword) {
      setLoading(false);
      setError('Passwörter stimmen nicht überein.');
      return;
    }

    try {
      await TempRegistrationService.register({ email, token, password });
      setStep('done');
      setInfo('Registrierung erfolgreich. Sie können sich jetzt anmelden.');
      // Optional: Auto-Weiterleitung zum Login
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      setError(extractErrorMessage(err, 'Registrierung fehlgeschlagen.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Typography variant="h4" gutterBottom>Altsystem-Registrierung</Typography>
      <Typography variant="body1" gutterBottom>
        Bitte geben Sie Ihre E-Mail-Adresse ein, die im Altsystem hinterlegt war.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }}>{info}</Alert>}

      {step === 'verify' && (
        <Box component="form" onSubmit={handleVerify} noValidate>
          <TextField
            label="E-Mail-Adresse"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            required
            margin="normal"
          />
          <Button type="submit" variant="contained" color="primary" disabled={loading || !email}>
            Verifizieren
          </Button>
        </Box>
      )}

      {step === 'password' && (
        <Box component="form" onSubmit={handleRegister} noValidate>
          <TextField
            label="Neues Passwort"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            required
            margin="normal"
          />
          <TextField
            label="Passwort bestätigen"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            fullWidth
            required
            margin="normal"
          />
          <Button type="submit" variant="contained" color="primary" disabled={loading || !password || !confirmPassword}>
            Registrierung abschließen
          </Button>
        </Box>
      )}

      {step === 'done' && (
        <Typography variant="body1">Weiterleitung zum Login...</Typography>
      )}
    </Container>
  );
};

export default TempRegistration;

